import type { InitialData, Placeholder } from '@omq/types';

import { ConfigWrapper } from './config-wrapper';
import { CookieSessionWrapper } from './cookie-session-wrapper';
import { useInitialData } from '../hooks/initialize';
import { IntegrationStyle } from './integration-style';
import { useCookie } from '../hooks/cookie';

/**
 * Component properties.
 */
export type InitialDataProps<R extends InitialData> = {
  cookieIsEnabled?: boolean;
  cookieName: string;
  params?: {};
  placeholder: Placeholder;
  onReady: (data: R) => JSX.Element;

  // pre-rendered (SSR) container content shown while initial data loads,
  // instead of the Loading... placeholder, to avoid a flash on first init
  initialContent?: string | null;
};

/**
 * Wrapper component that handles initial data loading.
 *
 * @param category - id of category
 * @param cookieIsEnabled - flag to enable/disable cookies
 * @param cookieName - name of the cookie
 * @param onReady - handle result callback
 *
 * @returns {JSX.Element}
 */
export function InitialDataWrapper<T extends InitialData>({
  params,
  cookieIsEnabled,
  cookieName,
  onReady,
  placeholder,
  initialContent,
}: InitialDataProps<T>): JSX.Element {
  const [cookie] = useCookie(cookieName);

  // load initial data
  // if cookie is enabled & cookie is already set
  // re-use session
  const data = useInitialData<T>(placeholder, cookie != null ? cookie.session : null, params);

  if (data == null) {
    // keep server-rendered content visible during the first init instead of
    // flashing a loading placeholder over it
    return initialContent != null ? (
      <div style={{ display: 'contents' }} dangerouslySetInnerHTML={{ __html: initialContent }} />
    ) : (
      <>{'Loading...'}</>
    );
  }

  return (
    <ConfigWrapper config={data.config}>
      <CookieSessionWrapper
        name={cookieName}
        sessionId={data.session.id}
        cookieIsEnabled={
          cookieIsEnabled != null ? cookieIsEnabled : data.config.isDefaultCookieActive
        }>
        <IntegrationStyle style={data.style} />
        {onReady(data)}
      </CookieSessionWrapper>
    </ConfigWrapper>
  );
}
